import { createCacheKey, ENDPOINTS, useCreatePayload } from 'hammock';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { useQueries } from 'react-query';

import { AnalyticQuery, QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { createUseQuery } from '@candu-ai/analytics/utils';
import { queryClient } from 'commons/fleet/queryClient';

export type Params = {
  queryId: QueryId;
  queryParams: QueryParamUrlObject;
  aggregateQueryId?: QueryId;
};

export const useGetAnalyticsQuery = <T>({
  queryId,
  aggregateQueryId,
  queryParams,
}: Params): [
  AnalyticQuery<T> | undefined,
  AnalyticQuery<T> | undefined,
  () => Promise<void>,
  boolean,
] => {
  const { get } = useCreatePayload(ENDPOINTS.analyticQueries);

  const [isRefetching, setIsRefetching] = useState(false);

  const query = queryParams || {};

  const seriesPayload = get({
    id: queryId,
    query,
  });

  const aggregatePayload = get({
    id: aggregateQueryId || '',
    query,
  });

  const [seriesResult, aggregateResult] = useQueries([
    createUseQuery<T>(seriesPayload, {}, isRefetching),
    createUseQuery<T>(aggregatePayload, { enabled: !!aggregateQueryId }, isRefetching),
  ]);

  const onRefreshQuery = useCallback(async () => {
    setIsRefetching(true);

    // invalidate queries so they can be refetched
    await queryClient.invalidateQueries({
      predicate: (q) => {
        if (aggregateQueryId) {
          return (
            isEqual(q.queryKey, createCacheKey(seriesPayload)) ||
            isEqual(q.queryKey, createCacheKey(aggregatePayload))
          );
        }

        return isEqual(q.queryKey, createCacheKey(seriesPayload));
      },
    });

    await Promise.all([seriesResult.refetch(), aggregateResult.refetch()]);

    setIsRefetching(false);
  }, [seriesResult, aggregateResult, setIsRefetching]);

  const [seriesData, aggregateData] = useMemo(
    () => [seriesResult.data, aggregateResult?.data || seriesResult.data],
    [seriesResult, aggregateResult, queryId, aggregateQueryId],
  );

  const isLoading =
    seriesResult.isLoading ||
    seriesResult.isFetching ||
    aggregateResult.isLoading ||
    aggregateResult.isFetching;

  const hasError =
    seriesResult.isLoadingError ||
    seriesResult.isRefetchError ||
    aggregateResult.isLoadingError ||
    aggregateResult.isRefetchError;

  const isLoadingQuery = isLoading && !hasError;

  return [seriesData, aggregateData, onRefreshQuery, isLoadingQuery];
};
